import dayjs from 'dayjs';

import { useState, useEffect } from 'react';

import { Button, Modal, Space, DatePicker, Upload, message, Row, Col, Input, InputNumber, Select } from 'antd';

import apiConfig from '../../API_Utils/APIUtil/ApiConfig';
import CallApi from '../../API_Utils/APIUtil/CallApi';
import { getError } from '../../clientUtils';
import * as Constants from '../../constants';
import { DISPATCH_MODE, DISPATCH_MODE_OPTIONS, isSelfDispatchMode, sortTransportersByName } from '../../Utils/soLogistics';

const DeliveryDetailsModal = ({
  isOpen,
  onOk,
  onCancel,
  loading,
  dispatchDate,
  requiresProofOfDelivery = true,
  onFileUpload,
  isFileMandatory = true,
  allowMultipleFiles = false,
  showLogisticsFields = false,
  logisticsMandatory = false,
  sellerName,
  lockToSelf = false
}) => {
  const [date, setDate] = useState('');
  const [file, setFile] = useState({});
  const [files, setFiles] = useState([]);
  const [boxCount, setBoxCount] = useState(null);
  const [remarks, setRemarks] = useState(null);
  const [vehicleNumber, setVehicleNumber] = useState('');
  const [trackingCarrierName, setTrackingCarrierName] = useState(null);
  const [transporters, setTransporters] = useState([]);
  const [dispatchMode, setDispatchMode] = useState(null);

  useEffect(() => {
    if (!isOpen || !lockToSelf) return;
    setDispatchMode(DISPATCH_MODE.SELF);
    setTrackingCarrierName(sellerName);
  }, [isOpen, lockToSelf, sellerName]);

  useEffect(() => {
    if (!showLogisticsFields || !isOpen) return;
    CallApi(
      apiConfig.MDM.transport,
      undefined,
      undefined,
      undefined,
      (data) => setTransporters(data || []),
      (error) => message.error(getError(error))
    );
  }, [showLogisticsFields, isOpen]);

  const handleDelivery = () => {
    const proofOfDelivery = allowMultipleFiles ? files : file;
    onOk(
      date,
      proofOfDelivery,
      boxCount,
      remarks,
      vehicleNumber,
      isSelfDispatchMode(dispatchMode) ? sellerName : trackingCarrierName,
      dispatchMode
    );
  };

  const handleDateChange = (selectedDate) => {
    setDate(selectedDate?.toISOString());
  };

  const handlePOD = async (selectedFile) => {
    const isLt5M = selectedFile.size / 1024 / 1024 < 5;
    if (!isLt5M) {
      message.error('Error: File size exceeds 5 MB');
      return false;
    }
    if (!['application/pdf', 'image/jpg', 'image/jpeg', 'image/png'].includes(selectedFile.type)) {
      message.error('You can only upload PDF or Image file');
      return false;
    }

    // If onFileUpload is provided, use it to upload and get file_url
    if (onFileUpload) {
      try {
        const uploadedFile = await onFileUpload(selectedFile);
        const fileWithUrl = { ...selectedFile, file_url: uploadedFile?.file_url || uploadedFile };
        
        if (allowMultipleFiles) {
          setFiles([...files, fileWithUrl]);
        } else {
          setFile(fileWithUrl);
        }
      } catch (error) {
        message.error('File upload failed');
        return false;
      }
    } else {
      // Fallback: store file without upload (for backward compatibility)
      if (allowMultipleFiles) {
        setFiles([...files, selectedFile]);
      } else {
        setFile(selectedFile);
      }
    }
    return false;
  };

  const handleFileRemove = (fileToRemove) => {
    if (allowMultipleFiles) {
      setFiles(files.filter(f => f.uid !== fileToRemove.uid));
    } else {
      setFile({});
    }
  };

  const isDisabled = () => {
    if (loading || !date) return true;
    if (requiresProofOfDelivery && isFileMandatory) {
      if (allowMultipleFiles && files.length === 0) return true;
      if (!allowMultipleFiles && !Object.keys(file).length) return true;
    }
    if (!boxCount) return true;
    if (showLogisticsFields && !dispatchMode) return true;
    if (showLogisticsFields && logisticsMandatory && (!vehicleNumber?.trim() || (isSelfDispatchMode(dispatchMode) ? !sellerName?.trim() : !trackingCarrierName))) return true;
    return false;
  };

  const disabledDate = (current) => current.isBefore(dayjs(dispatchDate));

  return (
    <Modal
      footer={[
        <Button key="cancel" onClick={onCancel}>
          Cancel
        </Button>,
        <Button key="submit" disabled={isDisabled()} loading={loading} onClick={handleDelivery} type="primary">
          Mark as Delivered
        </Button>
      ]}
      onCancel={onCancel}
      open={isOpen}
      title="Delivery Details">
      <Space className="w-100" direction="vertical" size="large">
        <Row>
          <Col className="required-after" span={8}>
            Delivery Date
          </Col>
          <Col span={16}>
            <DatePicker disabledDate={disabledDate} format={Constants.DATE_FORMAT} onChange={handleDateChange} />
          </Col>
        </Row>
        {!!requiresProofOfDelivery && (
          <Row>
            <Col className={isFileMandatory ? 'required-after' : ''} span={8}>
              Proof of Delivery
            </Col>
            <Col span={16}>
              <Upload
                accept="application/pdf, image/jpg, image/jpeg, image/png"
                beforeUpload={handlePOD}
                maxCount={allowMultipleFiles ? undefined : 1}
                multiple={allowMultipleFiles}
                onRemove={handleFileRemove}>
                <Button disabled={!allowMultipleFiles && !!Object.keys(file).length} size="large">
                  Upload
                </Button>
              </Upload>
            </Col>
          </Row>
        )}
        <Row>
          <Col className="required-after" span={8}>
            Box Count
          </Col>
          <Col span={16}>
            <InputNumber min={1} onChange={(val) => setBoxCount(val)} />
          </Col>
        </Row>
        {!!showLogisticsFields && (
          <>
            <Row>
              <Col className="required-after" span={8}>
                Delivery Mode
              </Col>
              <Col span={16}>
                <Select
                  className="w-100"
                  disabled={lockToSelf}
                  onChange={(value) => {
                    setDispatchMode(value);
                    if (isSelfDispatchMode(value)) {
                      setTrackingCarrierName(sellerName);
                    } else {
                      setTrackingCarrierName(null);
                    }
                  }}
                  options={DISPATCH_MODE_OPTIONS}
                  placeholder="Select delivery mode"
                  value={dispatchMode}
                />
              </Col>
            </Row>
            <Row>
              <Col className={logisticsMandatory ? 'required-after' : ''} span={8}>
                Vehicle Number
              </Col>
              <Col span={16}>
                <Input onChange={(e) => setVehicleNumber(e.target.value.trim())} value={vehicleNumber} />
              </Col>
            </Row>
            <Row>
              <Col className={logisticsMandatory ? 'required-after' : ''} span={8}>
                Transporter Name
              </Col>
              <Col span={16}>
                {isSelfDispatchMode(dispatchMode) ? (
                  <Input disabled value={sellerName} />
                ) : (
                  <Select
                    className="w-100"
                    onChange={(value) => setTrackingCarrierName(value)}
                    options={sortTransportersByName(transporters).map((transporter) => ({
                      label: transporter?.registered_transport_name,
                      value: transporter?.registered_transport_name
                    }))}
                    value={trackingCarrierName}
                  />
                )}
              </Col>
            </Row>
          </>
        )}
        <Row>
          <Col span={8}>Remarks</Col>
          <Col span={16}>
            <Input onChange={(e) => setRemarks(e.target.value)} />
          </Col>
        </Row>
      </Space>
    </Modal>
  );
};

export default DeliveryDetailsModal;
